import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit'
import isEqual from 'lodash.isequal'
import {AnalyticsDetailState, POLICY_TYPE} from 'interfaces'
import {
  resetAnalyticsDetailState,
  setAnalyticsDetailCategory,
  setAnalyticsDetailClassifications,
  setAnalyticsDetailFilters,
  setAnalyticsDetailPolicyTypes,
  setAnalyticsDetailScope,
  setSortAscending,
} from 'features/Analytics/Detail/analyticsDetailAction'

const initialState: AnalyticsDetailState = {
  policyType: [],
  resourceType: [],
  filterDetails: {},
  sortAscending: false,
}

const analyticDetailSlice = createSlice({
  name: 'analyticDetail',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<AnalyticsDetailState>) =>
    builder
      .addCase(setAnalyticsDetailFilters, (state, action) => ({
        ...state,
        selectedCategory: undefined,
        policyType: action?.payload?.policyType || [],
        resourceType:
          action?.payload?.resourceType || initialState.resourceType,
        filterDetails:
          action?.payload?.filterDetails || initialState.filterDetails,
      }))
      .addCase(setAnalyticsDetailScope, (state, {payload}) => {
        const {userEmail, groupEmail, city, country} =
          payload.filterDetails ?? {}
        if (
          state.selectedCategory ||
          state.filterDetails.category ||
          state.filterDetails.classifications?.length
        ) {
          state.policyType = []
        }
        delete state.selectedCategory
        const filterDetails = {
          ...(userEmail ? {userEmail} : {}),
          ...(groupEmail ? {groupEmail} : {}),
          ...(city ? {city} : {}),
          ...(country ? {country} : {}),
        }
        if (!isEqual(state.filterDetails, filterDetails)) {
          state.filterDetails = filterDetails
        }
        if (!isEqual(state.resourceType, payload.resourceType ?? [])) {
          state.resourceType = payload.resourceType ?? []
        }
      })
      .addCase(setAnalyticsDetailCategory, (state, {payload}) => {
        if (!payload.category.trim()) return
        if (!isEqual(state.policyType, [payload.policyType])) {
          state.policyType = [payload.policyType]
        }
        state.selectedCategory = payload.category
        if (state.resourceType.length) state.resourceType = []
        // Fixed Malware/DLP labels are filtered by type, not by category.
        const filterDetails =
          payload.policyType === POLICY_TYPE.Policy ||
          payload.policyType === POLICY_TYPE.CAC
            ? {category: payload.category}
            : {}
        if (!isEqual(state.filterDetails, filterDetails))
          state.filterDetails = filterDetails
      })
      .addCase(setAnalyticsDetailPolicyTypes, (state, {payload}) => {
        if (isEqual(state.policyType, payload)) return
        state.policyType = payload
        delete state.selectedCategory
        // Broadening the type selection would let unrelated DLP rows through
        // the backend's category filter. Clear that filter explicitly in the UI.
        delete state.filterDetails.category
        if (payload.length !== 1 || payload[0] !== POLICY_TYPE.DLP) {
          delete state.filterDetails.classifications
        }
      })
      .addCase(setAnalyticsDetailClassifications, (state, {payload}) => {
        if (payload.length) {
          if (!isEqual(state.policyType, [POLICY_TYPE.DLP]))
            state.policyType = [POLICY_TYPE.DLP]
          if (state.resourceType.length) state.resourceType = []
          delete state.selectedCategory
          const filterDetails = {classifications: payload}
          if (!isEqual(state.filterDetails, filterDetails))
            state.filterDetails = filterDetails
        } else if (state.filterDetails.classifications?.length) {
          delete state.filterDetails.classifications
          state.policyType = []
        }
      })
      .addCase(setSortAscending, (state, action) => {
        state.sortAscending = action.payload
      })
      .addCase(resetAnalyticsDetailState, () => ({
        ...initialState,
      }))
      .addDefaultCase(state => state),
})

export const {reducer: analyticDetailReducer, name: analyticDetailReducerName} =
  analyticDetailSlice

export type analyticDetailReducerState = ReturnType<
  typeof analyticDetailReducer
>

// export analytics detail action types
export type TAnalyticsDetailActions =
  | ReturnType<typeof setAnalyticsDetailFilters>
  | ReturnType<typeof resetAnalyticsDetailState>
  | ReturnType<typeof setSortAscending>
  | ReturnType<typeof setAnalyticsDetailScope>
  | ReturnType<typeof setAnalyticsDetailCategory>
  | ReturnType<typeof setAnalyticsDetailPolicyTypes>
  | ReturnType<typeof setAnalyticsDetailClassifications>

export const analyticsDetailActions = {
  setPolicyTypeFilter: setAnalyticsDetailFilters,
  resetAnalyticsDetailState,
  setSortAscending,
}
